import type { Multitask } from '@composite/multitask/types';
import type { WorkOrder } from '@models';

type SelectedWorkOrderLike = {
  id: string;
  isMultiTask: boolean;
  multitaskWoIds?: Array<WorkOrder['id']>;
  multitaskWorkOrderIds?: Array<WorkOrder['workOrderId']>;
  workOrderId?: WorkOrder['workOrderId'];
  unitId: string;
};

type MultitaskRefLike = Multitask['workOrderRefs'][number];

export const normalizeBusinessWorkOrderIds = (
  workOrderIds: Array<WorkOrder['workOrderId']>
) =>
  Array.from(
    new Set(
      workOrderIds.filter(
        (id): id is NonNullable<WorkOrder['workOrderId']> => id != null
      )
    )
  );

export const normalizeWorkOrderInternalIds = (ids: Array<WorkOrder['id']>) =>
  Array.from(
    new Set(ids.filter((id): id is NonNullable<WorkOrder['id']> => Boolean(id)))
  );

export const getWorkOrderInternalId = (workOrder: WorkOrder) => workOrder.id;

export const getWorkOrderBusinessId = (
  workOrder: WorkOrder
): WorkOrder['workOrderId'] => {
  if (workOrder.currentState?.code === 'PRE') {
    return workOrder.parentWorkOrder?.workOrderId;
  }

  return workOrder.workOrderId;
};

export const getMultitaskBusinessWorkOrderIds = (multitask: Multitask) =>
  normalizeBusinessWorkOrderIds(
    multitask.workOrderRefs.map((workOrderRef) => workOrderRef.workOrderId)
  );

export const getMultitaskInternalWorkOrderIds = (multitask: Multitask) =>
  normalizeWorkOrderInternalIds(
    multitask.workOrderRefs.map((workOrderRef) => workOrderRef.id)
  );

export const getSelectedBusinessWorkOrderIds = (
  selected: SelectedWorkOrderLike[]
) =>
  normalizeBusinessWorkOrderIds(
    selected.flatMap((item) => {
      if (item.isMultiTask) {
        return item.multitaskWorkOrderIds ?? [];
      }

      return [item.workOrderId];
    })
  );

export const getSelectedInternalWorkOrderIds = (
  selected: SelectedWorkOrderLike[]
) =>
  normalizeWorkOrderInternalIds(
    selected.flatMap((item) => {
      if (item.isMultiTask) {
        return item.multitaskWoIds ?? [];
      }

      return [item.id];
    })
  );

export const mergeBulkSelectionWithMultitaskRefs = <
  TSelected extends SelectedWorkOrderLike
>(
  selected: TSelected[],
  workOrderRefs: MultitaskRefLike[],
  unitId: string
): TSelected[] => {
  const selectedById = new Map<string, TSelected>();

  selected.forEach((item) => {
    selectedById.set(item.id, item);
  });

  workOrderRefs.forEach((workOrderRef) => {
    if (
      !workOrderRef.id ||
      !workOrderRef.workOrderId ||
      selectedById.has(workOrderRef.id)
    ) {
      return;
    }

    selectedById.set(workOrderRef.id, {
      id: workOrderRef.id,
      isMultiTask: false,
      workOrderId: workOrderRef.workOrderId,
      unitId,
    } as TSelected);
  });

  return Array.from(selectedById.values());
};

export const replaceBulkSelectionWithMultitaskRefs = <
  TSelected extends SelectedWorkOrderLike
>(
  selected: TSelected[],
  multitaskKey: string,
  workOrderRefs: MultitaskRefLike[],
  unitId: string
): TSelected[] =>
  mergeBulkSelectionWithMultitaskRefs(
    selected.filter((item) => item.id !== multitaskKey),
    workOrderRefs,
    unitId
  );
